import { InsuranceCoverageMonitorStatus } from "@wingspanhq/benefits/dist/src/lib/interfaces/insuranceCoverageMonitor";
import { IRequirementResponse } from "@wingspanhq/payments/dist/interfaces";
import { InsuranceCoverageMonitor } from "../../../../../../services/api/insurance/monitor/types";
import {
  getRequirementDefinitionDataItemProps,
  getRequirementDefinitionTableCellProps
} from "../../../../../RequirementDefinitions/utils";
import { selectorRequirementEngagementsSize } from "../../../../selectors/selectorRequirementEngagementsSize";
import { useRequirementDefaultActions } from "../../../../hooks/useRequirementDefaultActions";
import {
  CellsConfig,
  ContextItem,
  RequirementContextType,
  RequirementTableCallback
} from "../../../../types";
import { getInsuranceCoveragePayeeRequirementStatus } from "../helpers/getInsuranceCoveragePayeeRequirementStatus";
import { getInsuranceCoverageProgressSteps } from "../helpers/getInsuranceCoverageProgressSteps";
import { getInsuranceCoverageRequirementName } from "../helpers/getInsuranceCoverageRequirementName";
import { InsuranceCoverageDetailsWidgetContainer } from "../../components/InsuranceCoverageDetailsWidgetContainer";
import { InsuranceCoverageDetails } from "../../components/InsuranceCoverageDetails";
import {
  WSPageToolbarAction,
  WSPoster
} from "@wingspanhq/fe-component-library";
import { selectorPolicyTypesFromSettings } from "../../selectors/selectorPolicyTypesFromSettings";

export const usePayeeInsuranceCoverageConfig = (
  callback: RequirementTableCallback
): CellsConfig<
  IRequirementResponse,
  ContextItem,
  InsuranceCoverageMonitor | undefined
> => {
  const requirementDefaultActions = useRequirementDefaultActions(
    RequirementContextType.Payee,
    callback
  );

  return {
    isActionRequired: () => {
      return false;
    },
    requirementName: (requirement, _context, insuranceCoverage) =>
      getInsuranceCoverageRequirementName(
        insuranceCoverage,
        requirement.name || "Insurance Coverage"
      ),

    requirementInstructions:
      (requirement, _context, insuranceCoverage) => (instructions: string) => {
        if (
          insuranceCoverage?.status === InsuranceCoverageMonitorStatus.Complete
        ) {
          return "Your insurance coverage has been submitted successfully.";
        }
        return (
          instructions || "" || "Please provide proof of insurance coverage."
        );
      },

    requirementDetails: (requirement, context, insuranceCoverage) => {
      if (requirement.payerOverrideStatus === "Incomplete") {
        return (
          <InsuranceCoverageDetails
            context={RequirementContextType.Payee}
            description="The policy submitted by the contractor was rejected by you."
            requirement={requirement}
            insuranceCoverageMonitor={insuranceCoverage!}
            pill={{
              theme: "error",
              text: "Rejected",
              icon: true
            }}
          />
        );
      } else if (
        requirement.status === "Complete" ||
        insuranceCoverage?.status === "Complete"
      ) {
        return (
          <InsuranceCoverageDetails
            context={RequirementContextType.Payee}
            description="A policy from the contractor’s resources matched this requirement."
            requirement={requirement}
            insuranceCoverageMonitor={insuranceCoverage!}
            pill={{
              theme: "success",
              text: "Passed",
              icon: true
            }}
          />
        );
      } else if (insuranceCoverage?.status === "ForceMatched") {
        return (
          <InsuranceCoverageDetails
            context={RequirementContextType.Payee}
            description="The contractor submitted a partially matched policy for your approval"
            requirement={requirement}
            insuranceCoverageMonitor={insuranceCoverage}
            pill={{
              theme: "warning",
              text: "Partial match",
              icon: true
            }}
          />
        );
      } else {
        return {
          header: {
            label: {
              text: requirement.name
            },
            value: {
              pill: {
                theme: "neutral",
                text: "Not started",
                icon: "block"
              }
            }
          },
          data: [],
          children: (
            <WSPoster
              title="Awaiting proof of insurance"
              description="Waiting for contractor to provide their proof of insurance."
              type="documents"
            />
          )
        };
      }
    },

    requirementBasicInformation: (requirement, context, monitor) => {
      const status = getInsuranceCoveragePayeeRequirementStatus(
        requirement,
        monitor
      );

      return [
        getRequirementDefinitionDataItemProps({
          type: requirement.requirementType,
          policyType: selectorPolicyTypesFromSettings(monitor?.settings)
        }),
        {
          label: "Engagements",
          value: selectorRequirementEngagementsSize(requirement)
        },
        {
          label: {
            text: "Status"
          },
          value: "",
          hideValue: true,
          pill: status.pill,
          secondaryText: status.secondaryText
        }
      ];
    },

    requirementDetailsProgressWidget: (
      _requirement,
      _context,
      insuranceCoverage
    ) => {
      const steps = getInsuranceCoverageProgressSteps(insuranceCoverage);
      return steps;
    },

    requirementDetailsWidgets: (requirement, context, insuranceCoverage) => {
      return insuranceCoverage ? (
        <InsuranceCoverageDetailsWidgetContainer
          insuranceCoverage={insuranceCoverage}
          context={RequirementContextType.Payee}
          isInitialLoading={false}
        />
      ) : null;
    },

    toolbarActions: (requirement, context, monitor) => {
      const actions: WSPageToolbarAction[] = [];

      const status = getInsuranceCoveragePayeeRequirementStatus(
        requirement,
        monitor
      );

      const { extendToolbarActions } = requirementDefaultActions(
        requirement,
        context,
        monitor,
        status
      );

      return extendToolbarActions(actions);
    },

    nameCell: (requirement, _context, insuranceCoverage) => ({
      text: getInsuranceCoverageRequirementName(
        insuranceCoverage,
        requirement.name || "Insurance Coverage"
      )
    }),

    typeCell: (requirement, context, insuranceCoverage) =>
      getRequirementDefinitionTableCellProps({
        type: requirement.requirementType,
        policyType: selectorPolicyTypesFromSettings(insuranceCoverage?.settings)
      }),

    statusCell: (requirement, context, insuranceCoverage) => {
      return getInsuranceCoveragePayeeRequirementStatus(
        requirement,
        insuranceCoverage
      );
    }
  };
};
